import {noop} from 'lodash';
import React, {FC, PropsWithChildren, useContext} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {useToggle} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {
  EventHandler,
  PredicateOf,
  RendererOf,
} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {
  isTeammateNamespace,
  isTeamNamespace,
} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {PushContext} from '../../../../libs/shared/core/src/services/push/pushContext';
import {InboxesPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {DropdownSeparator} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownSeparator';
import {DropdownItemChevron} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemChevron';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {SimpleDropdown} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipOverflowCondition} from '../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {CellsConversationsDragItem} from '../../../helpers/conversationCellHelpers';
import {VisualCustomView} from '../../../helpers/models/customViewHelpers';
import {CustomViewTypesEnum} from '../../../models/api/customViewModel';
import {TagModel} from '../../../models/api/tagModel';
import {PermissionDropdownItem} from '../../common/dropdowns/permissionDropdownItem';
import {PermissionSimpleDropdownItem} from '../../common/dropdowns/permissionSimpleDropdownItem';
import {CreateSubtagDropdownContainer} from '../../common/dropdowns/tags/createSubtagDropdownContainer';
import {EditTagDropdownContainer} from '../../common/dropdowns/tags/editTagDropdownContainer';
import {NavigationLineChevron} from '../../common/navigation/navigationLineChevron';
import {NavigationLineLabel} from '../../common/navigation/navigationLineLabel';
import {
  WorkspaceDragItem,
  WorkspaceDragSourceTypesEnum,
  WorkspaceItemDropHandler,
} from './dnd/workspaceDragDropTypes';
import {EditSidebarCopy} from './editor/copy/edit-sidebar-copy';
import {HideInSidebarCopy} from './editor/copy/hide-in-sidebar-copy';
import {WorkspacesEditorRarelyViewedBadge} from './editor/workspacesEditorRarelyViewedBadge';
import {renderHighlight} from './iconOrEmojiRenderer';
import {WorkspaceLineDropTarget} from './line/workspaceLineDropTarget';
import {WorkspaceLineMore, WorkspaceLoadingTypesEnum} from './line/workspaceLineMore';

/*
 * Props.
 */

interface WorkspaceCustomViewItemProps {
  panelId: InboxesPanelId;
  /** The custom view to display. */
  customView: VisualCustomView;

  // Props for the WorkspaceLine and WorkspaceLineDropTarget.
  to?: string;
  isSelected?: boolean;
  hasHover?: boolean;
  disableBackground?: boolean;
  canConversationsDrop?: PredicateOf<CellsConversationsDragItem>;
  onConversationsDrop?: (item: CellsConversationsDragItem) => void;
  canDrag: boolean;
  hasExpandedChildren: boolean;
  isLastItem: boolean;
  onWorkspaceItemDrop?: WorkspaceItemDropHandler;
  shouldAlphabetizeWorkspace: boolean;

  // Props for WorkspaceLineMore.
  badgeValue?: number;
  isUnread?: boolean;
  loadingType?: WorkspaceLoadingTypesEnum;

  /** Expand or collapse this custom view. */
  onToggleExpandClick?: () => void;
  /** Hide this custom view. */
  onHideClick?: () => void;
  /** Does the user have the ability to edit/delete. */
  hasEditPermission: boolean;
  shouldHideMoreMenu: boolean;
  onEditWorkspaces: (id: InboxesPanelId) => void;

  /** Is the workspace in edit mode. */
  isEditingWorkspace?: boolean;
  /** Should the checkbox be selected if we're in editing mode. */
  isChecked?: boolean;
  isRecentlyViewed?: boolean;
  /**
   * An optional click handler.
   * Note that the component already links to the custom view when not editing the workspace.
   */
  onClick?: (view: VisualCustomView) => void;
}

/*
 * Style.
 */

export const StyledLockDiv = styled.div`
  flex-shrink: 0;
  margin-left: 4px;
`;

export const StyledLabelDiv = styled.div`
  display: flex;
  align-items: center;
`;

export const StyledTextDiv = styled.div`
  flex-shrink: 1;
  min-width: 0;
`;

/*
 * Component.
 */

export const WorkspaceCustomViewItem: FC<PropsWithChildren<WorkspaceCustomViewItemProps>> = (props) => {
  const {isConnected} = useContext(PushContext);
  const {customView, onHideClick, shouldHideMoreMenu, isEditingWorkspace, isSelected} = props;
  const onClick = () => (props.onClick || noop)(customView);

  const [isCreateTagOpen, toggleIsCreateTagOpen] = useToggle();
  const [isEditTagOpen, toggleIsEditTagOpen] = useToggle();
  const theme = useUserTheme();

  // Context menu.
  const renderMoreMenu: RendererOf<PopoverTransferProps> = (popoverProps) => {
    /*
     * Event handlers.
     */

    const resetToMainView = () => {
      if (isCreateTagOpen) {
        toggleIsCreateTagOpen();
      }

      if (isEditTagOpen) {
        toggleIsEditTagOpen();
      }
    };

    const onRequestClose = () => {
      popoverProps.onRequestClose(); // Close the dropdown
      resetToMainView(); // Reset back to List
    };

    const onCreateSelect: EventHandler = (e) => {
      e.preventDefault(); // Keep the dropdown open
      toggleIsCreateTagOpen(); // Switch to Tag Create
    };

    const onEditSelect: EventHandler = (e) => {
      e.preventDefault(); // Keep the dropdown open
      toggleIsEditTagOpen(); // Switch to Tag edit
    };

    /*
     * Renders.
     */

    if (isCreateTagOpen) {
      return renderCreateTagDropdown(popoverProps, props, onRequestClose, resetToMainView);
    }

    if (isEditTagOpen) {
      return renderEditTagDialog(popoverProps, props, onRequestClose, resetToMainView);
    }

    return renderDropdown(popoverProps, props, onCreateSelect, onEditSelect, onHideClick);
  };

  const isDisabled = customView.type === CustomViewTypesEnum.TAG && !isConnected;

  const workspaceDragItem: WorkspaceDragItem | undefined = props.canDrag
    ? {
        type: WorkspaceDragSourceTypesEnum.PERSONAL_VIEW,
        namespace: customView.namespace,
        hasExpandedChildren: props.hasExpandedChildren,
        nestingLevel: customView.nestingLevel,
        id: customView.id,
        isLast: props.isLastItem,
      }
    : undefined;

  // Workspace line item.
  return (
    <WorkspaceLineDropTarget
      to={props.to}
      isSelected={props.isSelected}
      hasHover={props.hasHover}
      disableBackground={props.disableBackground}
      canConversationsDrop={props.canConversationsDrop}
      onConversationsDrop={props.onConversationsDrop}
      workspaceDragItem={workspaceDragItem}
      onWorkspaceItemDrop={props.onWorkspaceItemDrop}
      shouldAlphabetizeWorkspace={props.shouldAlphabetizeWorkspace}
      indentLevel={customView.nestingLevel}
      renderTooltip={() => customView.tag.name}
      isDisabled={isDisabled}
      interactionId={makeInteractionId(customView, Boolean(isEditingWorkspace))}
      onClick={onClick}
      shouldHidePreviewBadge={isEditingWorkspace}
    >
      {maybeRenderChevron(props)}
      {renderHighlight(customView.tag, isSelected, false)}
      <NavigationLineLabel {...props}>
        <StyledLabelDiv>
          <StyledTextDiv>
            <TooltipOverflowCondition>{customView.tag.name}</TooltipOverflowCondition>
          </StyledTextDiv>
          {maybeRenderLock(customView.tag, theme)}
          {props.isRecentlyViewed === false && <WorkspacesEditorRarelyViewedBadge />}
        </StyledLabelDiv>
      </NavigationLineLabel>
      <WorkspaceLineMore
        renderMenu={(!shouldHideMoreMenu && renderMoreMenu) || undefined}
        isDisabled={isDisabled}
        shouldShowOfflineIcon={isDisabled}
        badgeValue={props.badgeValue}
        isUnread={props.isUnread}
        loadingType={props.loadingType}
        isEditingWorkspace={props.isEditingWorkspace}
        isChecked={props.isChecked}
        shouldRemoveBadgeLeftSpacing={props.isRecentlyViewed === false}
      />
    </WorkspaceLineDropTarget>
  );
};

/*
 * Helpers.
 */

function renderDropdown(
  popoverProps: PopoverTransferProps,
  props: WorkspaceCustomViewItemProps,
  onCreateSelect: EventHandler,
  onEditSelect: EventHandler,
  onHideClick?: () => void,
) {
  const {isSystemTag} = props.customView.tag;
  const isTeamTag = isTeamNamespace(props.customView.tag.namespace);
  return (
    <SimpleDropdown {...popoverProps}>
      {/* TODO: Use PermissionCheck for access check instead of hasEditPermission prop. */}
      <PermissionDropdownItem onSelect={onEditSelect} isDisabled={!props.hasEditPermission}>
        <DropdownItemContent>
          <Trans id="customviewmenu-edittag">Edit tag</Trans>
        </DropdownItemContent>
        <DropdownItemChevron />
      </PermissionDropdownItem>
      {!isSystemTag && (
        <PermissionDropdownItem onSelect={onCreateSelect}>
          <DropdownItemContent>
            <Trans id="customviewmenu-createsubtag">Create subtag</Trans>
          </DropdownItemContent>
          <DropdownItemChevron />
        </PermissionDropdownItem>
      )}
      {onHideClick && (
        <PermissionSimpleDropdownItem onSelect={onHideClick}>
          <HideInSidebarCopy />
        </PermissionSimpleDropdownItem>
      )}
      {(!isSystemTag || !isTeamTag) && <DropdownSeparator />}
      <PermissionSimpleDropdownItem onSelect={() => props.onEditWorkspaces(props.panelId)}>
        <EditSidebarCopy />
      </PermissionSimpleDropdownItem>
    </SimpleDropdown>
  );
}

function renderCreateTagDropdown(
  popoverProps: PopoverTransferProps,
  props: WorkspaceCustomViewItemProps,
  onRequestClose: () => void,
  onBackButtonClick: () => void,
) {
  return (
    <CreateSubtagDropdownContainer
      {...props}
      {...popoverProps}
      parentTag={props.customView.tag}
      onRequestClose={onRequestClose}
      onBackButtonClick={onBackButtonClick}
    />
  );
}

function renderEditTagDialog(
  popoverProps: PopoverTransferProps,
  props: WorkspaceCustomViewItemProps,
  onRequestClose: () => void,
  onBackButtonClick: () => void,
) {
  return (
    <EditTagDropdownContainer
      {...props}
      {...popoverProps}
      tag={props.customView.tag}
      onRequestClose={onRequestClose}
      onBackButtonClick={onBackButtonClick}
    />
  );
}

function maybeRenderChevron(props: WorkspaceCustomViewItemProps) {
  const {customView, onToggleExpandClick} = props;
  if (!onToggleExpandClick || customView.children.length === 0) {
    return null;
  }

  return (
    <NavigationLineChevron
      {...props}
      onToggleExpandClick={onToggleExpandClick}
      isExpanded={customView.isExpanded}
    />
  );
}

export function maybeRenderLock(tag: TagModel, theme: UserTheme) {
  // Only render the lock for private tags.
  if (!isTeammateNamespace(tag.namespace)) {
    return null;
  }

  return (
    <StyledLockDiv>
      <Icon name="lockFilledTiny" color={theme.itemPrivateLock} />
    </StyledLockDiv>
  );
}

export function makeInteractionId(customView: VisualCustomView, isEditingWorkspace: boolean) {
  const {id} = customView.tag;
  return isEditingWorkspace ? `workspace-editor-tag-${id}` : `workspace-tag-${id}`;
}
